import { useState } from 'react';
import type { FormEvent } from 'react';

import { ApiError } from '../services/apiClient';
import {
  createExercise,
  deleteExercise,
  previewExercise,
  publishExercise,
  unpublishExercise,
  updateExercise,
} from '../features/instructor/instructorApi';
import type {
  Difficulty,
  ExerciseAuthoring,
  ExercisePreview,
  ExerciseRequest,
  ExerciseType,
  ValidatorType,
} from '../features/instructor/instructorApi';

const EXERCISE_TYPES: ExerciseType[] = ['SHELL_CHALLENGE', 'FILL_BLANK', 'MULTIPLE_CHOICE', 'OUTPUT_MATCH'];
const DIFFICULTIES: Difficulty[] = ['EASY', 'MEDIUM', 'HARD'];
const VALIDATOR_TYPES: ValidatorType[] = ['EXACT_MATCH', 'PATTERN_MATCH', 'CUSTOM_FUNCTION', 'MULTIPLE_CHOICE'];
const CUSTOM_FUNCTIONS = ['equals', 'equalsIgnoreCase', 'startsWith', 'endsWith', 'contains'];

interface ChoiceOption {
  id: string;
  text: string;
}

interface FormState {
  type: ExerciseType;
  title: string;
  description: string;
  points: number;
  difficulty: Difficulty;
  setupCommandsText: string;
  validatorType: ValidatorType;
  exactExpected: string;
  pattern: string;
  customFunction: string;
  customValue: string;
  options: ChoiceOption[];
  correctOptionIds: string[];
  solutionExplanation: string;
  hintsText: string;
  allowCommands: string;
  blockCommands: string;
}

const DEFAULT_OPTIONS: ChoiceOption[] = [
  { id: 'a', text: '' },
  { id: 'b', text: '' },
];

const DEFAULT_FORM: FormState = {
  type: 'SHELL_CHALLENGE',
  title: '',
  description: '',
  points: 10,
  difficulty: 'EASY',
  setupCommandsText: '',
  validatorType: 'EXACT_MATCH',
  exactExpected: '',
  pattern: '',
  customFunction: CUSTOM_FUNCTIONS[0],
  customValue: '',
  options: DEFAULT_OPTIONS,
  correctOptionIds: [],
  solutionExplanation: '',
  hintsText: '',
  allowCommands: '',
  blockCommands: '',
};

function linesToText(lines: string[] | undefined): string {
  return (lines ?? []).join('\n');
}

function textToLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

function readConfigText(config: Record<string, unknown>, key: string): string {
  const value = config[key];
  return typeof value === 'string' ? value : '';
}

function isChoiceOption(value: unknown): value is ChoiceOption {
  return typeof value === 'object' && value !== null && 'id' in value && 'text' in value;
}

function readOptions(config: Record<string, unknown>): ChoiceOption[] {
  const options = config.options;
  if (!Array.isArray(options)) {
    return DEFAULT_OPTIONS;
  }
  return options
    .filter(isChoiceOption)
    .map((option) => ({ id: String(option.id), text: String(option.text) }));
}

function readCorrectOptionIds(config: Record<string, unknown>): string[] {
  const value = config.correctOptionIds ?? config.expectedOptionIds;
  return Array.isArray(value) ? value.map((id) => String(id)) : [];
}

function readAllowBlock(config: Record<string, unknown>, key: string): string {
  const value = config[key];
  return Array.isArray(value) ? value.join(', ') : '';
}

function parseCommaList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

function buildInitialState(exercise: ExerciseAuthoring | null): FormState {
  if (!exercise) {
    return DEFAULT_FORM;
  }
  const config = exercise.validatorConfig;
  return {
    type: exercise.type,
    title: exercise.title,
    description: exercise.description,
    points: exercise.points,
    difficulty: exercise.difficulty,
    setupCommandsText: linesToText(exercise.setupCommands),
    validatorType: exercise.validatorType,
    exactExpected: readConfigText(config, 'expected'),
    pattern: readConfigText(config, 'pattern'),
    customFunction: readConfigText(config, 'function') || CUSTOM_FUNCTIONS[0],
    customValue: readConfigText(config, 'value'),
    options: readOptions(config),
    correctOptionIds: readCorrectOptionIds(config),
    solutionExplanation: exercise.solutionExplanation ?? '',
    hintsText: linesToText(exercise.hints),
    allowCommands: readAllowBlock(exercise.commandPolicy, 'allow'),
    blockCommands: readAllowBlock(exercise.commandPolicy, 'block'),
  };
}

function buildValidatorConfig(form: FormState): Record<string, unknown> {
  switch (form.validatorType) {
    case 'EXACT_MATCH':
      return { expected: form.exactExpected };
    case 'PATTERN_MATCH':
      return { pattern: form.pattern };
    case 'CUSTOM_FUNCTION':
      return { function: form.customFunction, value: form.customValue };
    case 'MULTIPLE_CHOICE':
      return {
        options: form.options.filter((option) => option.text.trim().length > 0),
        correctOptionIds: form.correctOptionIds,
      };
    default:
      return {};
  }
}

function buildRequest(form: FormState): ExerciseRequest {
  return {
    type: form.type,
    title: form.title,
    description: form.description,
    points: form.points,
    difficulty: form.difficulty,
    setupCommands: textToLines(form.setupCommandsText),
    validatorType: form.validatorType,
    validatorConfig: buildValidatorConfig(form),
    solutionExplanation: form.solutionExplanation,
    hints: textToLines(form.hintsText),
    commandPolicy: {
      allow: parseCommaList(form.allowCommands),
      block: parseCommaList(form.blockCommands),
    },
  };
}

function readApiErrorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? (error.body?.message ?? fallback) : fallback;
}

function readApiErrorDetails(error: unknown): string[] {
  return error instanceof ApiError ? (error.body?.details ?? []) : [];
}

function ErrorAlert({ message, details }: { message: string | null; details: string[] }) {
  if (!message) {
    return null;
  }
  return (
    <div role="alert">
      <p>{message}</p>
      {details.length > 0 && (
        <ul>
          {details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ExercisePreviewPanel({ preview }: { preview: ExercisePreview | null }) {
  if (!preview) {
    return null;
  }
  return (
    <section
      aria-label="Aperçu étudiant"
      style={{ border: '1px solid var(--color-border-strong)', borderRadius: '0.75rem', padding: '1rem' }}
    >
      <h3>{preview.title}</h3>
      <p>{preview.description}</p>
      <p>{preview.points} points</p>
      {preview.options.length > 0 && (
        <ul>
          {preview.options.map((option) => (
            <li key={option.id}>{option.text}</li>
          ))}
        </ul>
      )}
      {preview.solutionExplanation && <p>Solution : {preview.solutionExplanation}</p>}
    </section>
  );
}

interface MultipleChoiceFieldsProps {
  options: ChoiceOption[];
  correctOptionIds: string[];
  onOptionTextChange: (index: number, text: string) => void;
  onToggleCorrect: (optionId: string) => void;
  onAddOption: () => void;
}

function MultipleChoiceFields({
  options,
  correctOptionIds,
  onOptionTextChange,
  onToggleCorrect,
  onAddOption,
}: MultipleChoiceFieldsProps) {
  return (
    <fieldset>
      <legend>Choix de réponse</legend>
      {options.map((option, index) => (
        <div key={option.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="checkbox"
            aria-label={`Bonne réponse ${index + 1}`}
            checked={correctOptionIds.includes(option.id)}
            onChange={() => onToggleCorrect(option.id)}
          />
          <input
            type="text"
            aria-label={`Texte du choix ${index + 1}`}
            value={option.text}
            onChange={(event) => onOptionTextChange(index, event.target.value)}
          />
        </div>
      ))}
      <button type="button" onClick={onAddOption}>
        Ajouter un choix
      </button>
    </fieldset>
  );
}

interface ValidatorConfigFieldsProps {
  form: FormState;
  updateField: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
}

function ValidatorConfigFields({ form, updateField }: ValidatorConfigFieldsProps) {
  function handleOptionTextChange(index: number, text: string) {
    updateField(
      'options',
      form.options.map((option, candidateIndex) =>
        candidateIndex === index ? { ...option, text } : option,
      ),
    );
  }

  function handleToggleCorrect(optionId: string) {
    updateField(
      'correctOptionIds',
      form.correctOptionIds.includes(optionId)
        ? form.correctOptionIds.filter((id) => id !== optionId)
        : [...form.correctOptionIds, optionId],
    );
  }

  function handleAddOption() {
    updateField('options', [
      ...form.options,
      { id: String.fromCharCode(97 + form.options.length), text: '' },
    ]);
  }

  switch (form.validatorType) {
    case 'EXACT_MATCH':
      return (
        <label>
          Réponse attendue
          <input
            type="text"
            value={form.exactExpected}
            onChange={(event) => updateField('exactExpected', event.target.value)}
          />
        </label>
      );
    case 'PATTERN_MATCH':
      return (
        <label>
          Expression régulière
          <input
            type="text"
            value={form.pattern}
            onChange={(event) => updateField('pattern', event.target.value)}
          />
        </label>
      );
    case 'CUSTOM_FUNCTION':
      return (
        <>
          <label>
            Fonction
            <select
              value={form.customFunction}
              onChange={(event) => updateField('customFunction', event.target.value)}
            >
              {CUSTOM_FUNCTIONS.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Valeur
            <input
              type="text"
              value={form.customValue}
              onChange={(event) => updateField('customValue', event.target.value)}
            />
          </label>
        </>
      );
    case 'MULTIPLE_CHOICE':
      return (
        <MultipleChoiceFields
          options={form.options}
          correctOptionIds={form.correctOptionIds}
          onOptionTextChange={handleOptionTextChange}
          onToggleCorrect={handleToggleCorrect}
          onAddOption={handleAddOption}
        />
      );
    default:
      return null;
  }
}

interface ExerciseActionsProps {
  exercise: ExerciseAuthoring | null;
  isLoadingPreview: boolean;
  onPublishToggle: () => void;
  onPreview: () => void;
  onDelete: () => void;
}

function ExerciseActions({ exercise, isLoadingPreview, onPublishToggle, onPreview, onDelete }: ExerciseActionsProps) {
  if (!exercise) {
    return null;
  }
  const publishLabel = exercise.status === 'PUBLISHED' ? 'Dépublier' : 'Publier';
  const previewLabel = isLoadingPreview ? 'Chargement de l’aperçu…' : 'Aperçu étudiant';
  return (
    <>
      <button type="button" onClick={onPublishToggle}>
        {publishLabel}
      </button>
      <button type="button" onClick={onPreview} disabled={isLoadingPreview}>
        {previewLabel}
      </button>
      <button type="button" onClick={onDelete}>
        Supprimer
      </button>
    </>
  );
}

interface ExerciseEditorProps {
  chapterId: string;
  exercise: ExerciseAuthoring | null;
  onSaved: () => void;
  onCancel: () => void;
  onDeleted?: () => void;
}

function ExerciseEditor({ chapterId, exercise, onSaved, onCancel, onDeleted }: ExerciseEditorProps) {
  const isEditing = exercise !== null;

  const [form, setForm] = useState<FormState>(() => buildInitialState(exercise));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preview, setPreview] = useState<ExercisePreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setErrorDetails([]);
    setIsSubmitting(true);

    try {
      if (exercise) {
        await updateExercise(exercise.id, buildRequest(form));
      } else {
        await createExercise(chapterId, buildRequest(form));
      }
      onSaved();
    } catch (error) {
      setErrorMessage(readApiErrorMessage(error, "Impossible d'enregistrer l'exercice."));
      setErrorDetails(readApiErrorDetails(error));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePublishToggle() {
    if (!exercise) {
      return;
    }
    setErrorMessage(null);
    setErrorDetails([]);
    try {
      await (exercise.status === 'PUBLISHED' ? unpublishExercise(exercise.id) : publishExercise(exercise.id));
      onSaved();
    } catch (error) {
      setErrorMessage(readApiErrorMessage(error, "Impossible de modifier l'état de publication."));
      setErrorDetails(readApiErrorDetails(error));
    }
  }

  async function handleDelete() {
    if (!exercise) {
      return;
    }
    setErrorMessage(null);
    try {
      await deleteExercise(exercise.id);
      onDeleted?.();
    } catch (error) {
      setErrorMessage(readApiErrorMessage(error, "Impossible de supprimer l'exercice."));
    }
  }

  async function handlePreview() {
    if (!exercise) {
      return;
    }
    setIsLoadingPreview(true);
    setErrorMessage(null);
    try {
      setPreview(await previewExercise(exercise.id));
    } catch (error) {
      setErrorMessage(readApiErrorMessage(error, "Impossible de charger l'aperçu."));
    } finally {
      setIsLoadingPreview(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <form onSubmit={handleSubmit} aria-label={isEditing ? "Modifier l'exercice" : 'Nouvel exercice'}>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <label>
            Type d'exercice
            <select value={form.type} onChange={(event) => updateField('type', event.target.value as ExerciseType)}>
              {EXERCISE_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Titre
            <input type="text" value={form.title} onChange={(event) => updateField('title', event.target.value)} required />
          </label>
          <label>
            Description
            <textarea
              value={form.description}
              onChange={(event) => updateField('description', event.target.value)}
              required
            />
          </label>
          <label>
            Points
            <input
              type="number"
              min={1}
              value={form.points}
              onChange={(event) => updateField('points', Number(event.target.value))}
              required
            />
          </label>
          <label>
            Difficulté
            <select
              value={form.difficulty}
              onChange={(event) => updateField('difficulty', event.target.value as Difficulty)}
            >
              {DIFFICULTIES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label>
            Commandes de préparation (une par ligne)
            <textarea
              value={form.setupCommandsText}
              onChange={(event) => updateField('setupCommandsText', event.target.value)}
            />
          </label>
          <label>
            Type de validation
            <select
              value={form.validatorType}
              onChange={(event) => updateField('validatorType', event.target.value as ValidatorType)}
            >
              {VALIDATOR_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>

          <ValidatorConfigFields form={form} updateField={updateField} />

          <label>
            Explication de la solution
            <textarea
              value={form.solutionExplanation}
              onChange={(event) => updateField('solutionExplanation', event.target.value)}
            />
          </label>
          <label>
            Indices (un par ligne)
            <textarea value={form.hintsText} onChange={(event) => updateField('hintsText', event.target.value)} />
          </label>
          <label>
            Commandes autorisées (séparées par des virgules)
            <input
              type="text"
              value={form.allowCommands}
              onChange={(event) => updateField('allowCommands', event.target.value)}
            />
          </label>
          <label>
            Commandes bloquées (séparées par des virgules)
            <input
              type="text"
              value={form.blockCommands}
              onChange={(event) => updateField('blockCommands', event.target.value)}
            />
          </label>

          <ErrorAlert message={errorMessage} details={errorDetails} />

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button type="submit" disabled={isSubmitting}>
              {isEditing ? 'Enregistrer' : "Créer l'exercice"}
            </button>
            <button type="button" onClick={onCancel}>
              Annuler
            </button>
            <ExerciseActions
              exercise={exercise}
              isLoadingPreview={isLoadingPreview}
              onPublishToggle={handlePublishToggle}
              onPreview={handlePreview}
              onDelete={handleDelete}
            />
          </div>
        </div>
      </form>

      <ExercisePreviewPanel preview={preview} />
    </div>
  );
}

export default ExerciseEditor;
